import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InsightsIcon from '@mui/icons-material/Insights';
import SensorsIcon from '@mui/icons-material/Sensors';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ApiIcon from '@mui/icons-material/Api';
import CellTowerIcon from '@mui/icons-material/CellTower';
import VerifiedIcon from '@mui/icons-material/Verified';
import HubIcon from '@mui/icons-material/Hub';
import { useTranslation } from 'react-i18next';
import heroImage from '../assets/physical_ai_dashboard.webp';
import detailImage from '../assets/physical_ai_detail.webp';
import heroTopImage from '../assets/fyra_suite_physical_ai.webp';
import agentImage from '../assets/fyra_agent.webp';
import conceptImage from '../assets/fyra_api_overview.webp';
import automotiveImage from '../assets/industry_automotive.webp';
import logisticsImage from '../assets/industry_logistics.webp';
import constructionImage from '../assets/industry_construction.webp';
import agricultureImage from '../assets/industry_agriculture.webp';

const DARK_BG = '#0B1F33';
const DARK_BG_SOFT = '#12304B';

const problems = [
  { key: 'item1', icon: <TroubleshootIcon fontSize="large" /> },
  { key: 'item2', icon: <TrendingDownIcon fontSize="large" /> },
  { key: 'item3', icon: <InsightsIcon fontSize="large" /> },
] as const;

const steps = [
  { key: 'step1', icon: <SensorsIcon fontSize="large" /> },
  { key: 'step2', icon: <SyncAltIcon fontSize="large" /> },
  { key: 'step3', icon: <ApiIcon fontSize="large" /> },
] as const;

const features = [
  { key: 'item1', image: heroImage },
  { key: 'item2', image: detailImage },
  { key: 'item3', image: agentImage },
] as const;

const industries = [
  { key: 'automotive', image: automotiveImage },
  { key: 'logistics', image: logisticsImage },
  { key: 'construction', image: constructionImage },
  { key: 'agriculture', image: agricultureImage },
] as const;

const proofs = [
  { key: 'item1', icon: <CellTowerIcon fontSize="large" /> },
  { key: 'item2', icon: <VerifiedIcon fontSize="large" /> },
  { key: 'item3', icon: <HubIcon fontSize="large" /> },
] as const;

interface SectionHeadingProps {
  overline: string;
  title: string;
  lead?: string;
  dark?: boolean;
}

/** セクション冒頭の英字ラベル・見出し・リード文 */
const SectionHeading: React.FC<SectionHeadingProps> = ({
  overline,
  title,
  lead,
  dark = false,
}) => (
  <Box mb={5} sx={{ color: dark ? '#fff' : 'text.primary' }}>
    <Typography
      variant="overline"
      component="div"
      sx={{
        letterSpacing: 3,
        fontWeight: 700,
        color: dark ? 'secondary.light' : 'secondary.main',
      }}
    >
      {overline}
    </Typography>
    <Typography
      variant="h2"
      component="h2"
      sx={{
        fontWeight: 700,
        fontSize: { xs: '1.6rem', md: '2.1rem' },
        lineHeight: 1.4,
        mb: lead ? 2 : 0,
      }}
    >
      {title}
    </Typography>
    {lead && (
      <Typography sx={{ color: 'inherit', lineHeight: 1.9, opacity: 0.9 }}>
        {lead}
      </Typography>
    )}
  </Box>
);

/** お問い合わせページへ遷移する CTA ボタン */
const ContactButton: React.FC<{ label: string }> = ({ label }) => (
  <Button
    component={RouterLink}
    to="/contact-form"
    variant="contained"
    color="secondary"
    size="large"
    endIcon={<ArrowForwardIcon />}
    sx={{ fontWeight: 700, textTransform: 'none', px: 4 }}
  >
    {label}
  </Button>
);

/** スクリーンショット画像を枠付きで表示する */
const Screenshot: React.FC<{ src: string; alt: string }> = ({ src, alt }) => (
  <CardMedia
    component="img"
    image={src}
    alt={alt}
    sx={{
      borderRadius: 2,
      border: 1,
      borderColor: 'rgba(255,255,255,0.15)',
      boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
    }}
  />
);

/** FYRA API 紹介ページ本体。フィジカルAI産業の現場担当者・経営層向けに構成する */
const FyraApiSection: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box
        sx={{
          background: `linear-gradient(160deg, ${DARK_BG} 0%, ${DARK_BG_SOFT} 60%, #165E83 100%)`,
          color: '#fff',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                component="div"
                sx={{ letterSpacing: 2, fontWeight: 700, opacity: 0.8 }}
              >
                {t('fyraApi.hero.overline')}
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '2.1rem', sm: '2.6rem', md: '3rem' },
                  lineHeight: 1.3,
                  mt: 1,
                  mb: 3,
                }}
              >
                {t('fyraApi.hero.ttl')}
              </Typography>
              <Typography
                sx={{
                  color: 'inherit',
                  lineHeight: 1.9,
                  fontSize: { md: '1.05rem' },
                  opacity: 0.9,
                  mb: 4,
                }}
              >
                {t('fyraApi.hero.sub')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <ContactButton label={t('fyraApi.hero.cta')} />
                <Button
                  href="#how-it-works"
                  variant="outlined"
                  size="large"
                  sx={{
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.5)',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': { borderColor: '#fff' },
                  }}
                >
                  {t('fyraApi.hero.cta2')}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Screenshot src={heroTopImage} alt={t('fyraApi.hero.caption')} />
              <Typography
                variant="caption"
                component="div"
                align="center"
                sx={{ mt: 1.5, opacity: 0.7 }}
              >
                {t('fyraApi.hero.caption')}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <SectionHeading
          overline={t('fyraApi.problem.overline')}
          title={t('fyraApi.problem.ttl')}
          lead={t('fyraApi.problem.lead')}
        />
        <Grid container spacing={3}>
          {problems.map((problem) => (
            <Grid item xs={12} md={4} key={problem.key}>
              <Card
                variant="outlined"
                sx={{ height: '100%', bgcolor: 'grey.50' }}
              >
                <CardContent sx={{ py: 4 }}>
                  <Box sx={{ color: 'secondary.main', mb: 2 }}>
                    {problem.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontWeight: 700, lineHeight: 1.4, mb: 1.5 }}
                  >
                    {t(`fyraApi.problem.${problem.key}.ttl`)}
                  </Typography>
                  <Typography sx={{ lineHeight: 1.8 }}>
                    {t(`fyraApi.problem.${problem.key}.desc`)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box id="how-it-works" sx={{ bgcolor: DARK_BG, color: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <SectionHeading
            dark
            overline={t('fyraApi.how.overline')}
            title={t('fyraApi.how.ttl')}
            lead={t('fyraApi.how.lead')}
          />
          <Screenshot src={conceptImage} alt={t('fyraApi.how.imgAlt')} />
          <Grid container spacing={4} mt={2}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={4} key={step.key}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box sx={{ color: 'secondary.light', pt: 0.5 }}>
                    {step.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      {index + 1}. {t(`fyraApi.how.${step.key}.ttl`)}
                    </Typography>
                    <Typography
                      sx={{ color: 'inherit', opacity: 0.85, lineHeight: 1.8 }}
                    >
                      {t(`fyraApi.how.${step.key}.desc`)}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <SectionHeading
          overline={t('fyraApi.features.overline')}
          title={t('fyraApi.features.ttl')}
        />
        {features.map((feature, index) => {
          const imageLeft = index % 2 === 0;
          return (
            <Grid
              container
              spacing={{ xs: 3, md: 6 }}
              alignItems="center"
              key={feature.key}
              sx={{ mb: { xs: 6, md: 10 } }}
            >
              <Grid
                item
                xs={12}
                md={7}
                sx={{ order: { xs: 1, md: imageLeft ? 0 : 1 } }}
              >
                <Screenshot
                  src={feature.image}
                  alt={t(`fyraApi.features.${feature.key}.caption`)}
                />
                <Typography
                  variant="caption"
                  component="div"
                  align="center"
                  sx={{ mt: 1, color: 'text.primary', opacity: 0.7 }}
                >
                  {t(`fyraApi.features.${feature.key}.caption`)}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                md={5}
                sx={{ order: { xs: 0, md: imageLeft ? 1 : 0 } }}
              >
                <Chip
                  label={t(`fyraApi.features.${feature.key}.chip`)}
                  color="secondary"
                  sx={{ fontWeight: 700, mb: 2 }}
                />
                <Typography
                  variant="h4"
                  component="h3"
                  sx={{ lineHeight: 1.4, mb: 2 }}
                >
                  {t(`fyraApi.features.${feature.key}.ttl`)}
                </Typography>
                <Typography sx={{ lineHeight: 1.9 }}>
                  {t(`fyraApi.features.${feature.key}.desc`)}
                </Typography>
              </Grid>
            </Grid>
          );
        })}
      </Container>

      <Box sx={{ bgcolor: 'grey.100' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <SectionHeading
            overline={t('fyraApi.industries.overline')}
            title={t('fyraApi.industries.ttl')}
            lead={t('fyraApi.industries.lead')}
          />
          <Grid container spacing={3}>
            {industries.map((industry) => (
              <Grid item xs={12} sm={6} key={industry.key}>
                <Card sx={{ height: '100%' }}>
                  <CardMedia
                    component="img"
                    image={industry.image}
                    alt={t(`fyraApi.industries.${industry.key}`)}
                  />
                  <CardContent>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 700 }}
                    >
                      {t(`fyraApi.industries.${industry.key}`)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Typography
            variant="caption"
            component="p"
            sx={{ mt: 2, color: 'text.primary', opacity: 0.7 }}
          >
            {t('fyraApi.industries.note')}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <SectionHeading
          overline={t('fyraApi.proof.overline')}
          title={t('fyraApi.proof.ttl')}
          lead={t('fyraApi.proof.lead')}
        />
        <Grid container spacing={3}>
          {proofs.map((proof) => (
            <Grid item xs={12} md={4} key={proof.key}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ py: 4 }}>
                  <Box sx={{ color: 'secondary.main', mb: 2 }}>
                    {proof.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{ fontWeight: 700, lineHeight: 1.4, mb: 1.5 }}
                  >
                    {t(`fyraApi.proof.${proof.key}.ttl`)}
                  </Typography>
                  <Typography sx={{ lineHeight: 1.8 }}>
                    {t(`fyraApi.proof.${proof.key}.desc`)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Card variant="outlined" sx={{ mt: 3, bgcolor: 'grey.50' }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{ fontWeight: 700, lineHeight: 1.4, mb: 1.5 }}
            >
              {t('fyraApi.proof.partner.ttl')}
            </Typography>
            <Typography sx={{ lineHeight: 1.8 }}>
              {t('fyraApi.proof.partner.desc')}
            </Typography>
          </CardContent>
        </Card>
      </Container>

      <Box sx={{ bgcolor: DARK_BG, color: '#fff' }}>
        <Container
          maxWidth="md"
          sx={{ py: { xs: 8, md: 10 }, textAlign: 'center' }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '2rem' },
              lineHeight: 1.4,
              mb: 2,
            }}
          >
            {t('fyraApi.cta.ttl')}
          </Typography>
          <Typography sx={{ color: 'inherit', lineHeight: 1.9, mb: 4 }}>
            {t('fyraApi.cta.desc')}
          </Typography>
          <ContactButton label={t('fyraApi.cta.btn')} />
        </Container>
      </Box>
    </Box>
  );
};

export default FyraApiSection;
